import { useEffect, useRef, useState, type KeyboardEvent, type ReactElement, type RefObject } from 'react';
import type { ReceiptView, SeatView } from '@nightshift/engine/client';

type ReceiptCursor = { seen: number | null; degraded: boolean };

/** Order both durable boundaries within each turn. */
function receiptOrder(receipt: ReceiptView): number {
  return (receipt.turn - 1) * 2 + (receipt.boundary === 'COMMIT' ? 1 : 0);
}

/** Recover the last acknowledged boundary for this seat. */
function readReceiptCursor(identity: string): ReceiptCursor {
  try {
    const raw = sessionStorage.getItem(`ns_turn_updates_${identity}`);
    const seen = raw === null ? null : Number(raw);
    if (seen !== null && (!Number.isSafeInteger(seen) || seen < 0)) throw new Error('Invalid turn update cursor');
    return { seen, degraded: false };
  } catch (error) {
    console.warn('[nightshift] Cannot restore turn update cursor', { identity, error });
    return { seen: null, degraded: true };
  }
}

/** Preserve unread receipts across reconnects and page reloads. */
function useReceiptCursor(identity: string): [ReceiptCursor, (receipt: ReceiptView) => void] {
  const [cursor, setCursor] = useState(() => readReceiptCursor(identity));
  const acknowledge = (receipt: ReceiptView) => {
    const seen = Math.max(cursor.seen ?? -1, receiptOrder(receipt));
    let degraded = cursor.degraded;
    try {
      sessionStorage.setItem(`ns_turn_updates_${identity}`, String(seen));
    } catch (error) {
      console.warn('[nightshift] Cannot save turn update cursor', { identity, error });
      degraded = true;
    }
    setCursor({ seen, degraded });
  };
  return [cursor, acknowledge];
}

/** Open after patient-sheet events and restore the previous focus. */
function useReceiptDialog(receiptId: string | undefined, visible: boolean): RefObject<HTMLDialogElement | null> {
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const node = dialog.current;
    if (!visible || !node) return;
    const focused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const opening = window.setTimeout(() => node.showModal(), 0);
    return () => {
      window.clearTimeout(opening);
      if (!node.open) return;
      node.close();
      if (focused?.isConnected && focused !== document.body) focused.focus();
      else {
        const main = document.querySelector<HTMLElement>('.player main');
        main?.setAttribute('tabindex', '-1');
        main?.focus();
      }
    };
  }, [visible, receiptId]);
  return dialog;
}

/** Keep keyboard navigation inside the native dialog. */
function trapReceiptFocus(event: KeyboardEvent<HTMLDialogElement>): void {
  if (event.key !== 'Tab') return;
  const controls = event.currentTarget.querySelectorAll<HTMLElement>('button, select');
  const first = controls[0], last = controls[controls.length - 1];
  if (event.shiftKey && (event.target === first || event.target === event.currentTarget)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (event.target === last || event.target === event.currentTarget)) {
    event.preventDefault();
    first.focus();
  }
}

/** Show only the already-filtered boundary events and personal outcomes. */
function ReceiptContents({ receipt }: { receipt: ReceiptView }): ReactElement {
  const status = { APPLIED: 'Completed', BLOCKED: 'Refused', CANCELLED: 'Cancelled', DELAYED: 'Delayed' };
  return <>
    {receipt.outcomes.length > 0 && <section><h3>Your actions</h3><ul>
      {receipt.outcomes.map(outcome => <li key={outcome.actionId}><strong>{status[outcome.status]}:</strong> {outcome.reason}</li>)}
    </ul></section>}
    {receipt.events.length > 0 && <section><h3>Your patients</h3><ul>
      {receipt.events.map((event, index) => <li key={`${event.type}:${index}`}>{event.text}</li>)}
    </ul></section>}
    {receipt.outcomes.length === 0 && receipt.events.length === 0 && <p>Nothing changed for your patients.</p>}
  </>;
}

/** Present durable boundary changes after the player finishes an open card. */
export function PhaseChanges({ v, connected, blocked, identity }: { v: SeatView; connected: boolean; blocked: boolean; identity: string }): ReactElement {
  const [cursor, acknowledge] = useReceiptCursor(identity);
  const [manual, setManual] = useState<string | null>(null);
  const unread = v.receipts.find(receipt => receiptOrder(receipt) > (cursor.seen ?? -1));
  const selected = v.receipts.find(receipt => receipt.id === manual);
  const receipt = manual ? selected ?? v.receipts.at(-1) : unread;
  const visible = connected && !blocked && !!receipt;
  const dialog = useReceiptDialog(receipt?.id, visible);
  const oldest = v.receipts[0];
  const gap = !!oldest && receiptOrder(oldest) > (cursor.seen ?? -1) + 1;
  const dismiss = () => {
    if (manual) setManual(null);
    else if (receipt) acknowledge(receipt);
  };
  return <>
    <div className="turn-updates">
      <button className="btn btn-small" disabled={!connected || blocked || v.receipts.length === 0} onClick={() => setManual(v.receipts.at(-1)!.id)}>Turn updates</button>
      {cursor.degraded && <p className="small muted" role="status">Updates may repeat after reloading because browser storage is unavailable.</p>}
    </div>
    {visible && receipt && <dialog className="phase-changes" ref={dialog} aria-labelledby="phase-changes-title" aria-describedby="phase-changes-context" onCancel={event => { event.preventDefault(); dismiss(); }} onKeyDown={trapReceiptFocus}>
      <header><h2 id="phase-changes-title">What changed</h2><button className="btn btn-quiet" onClick={dismiss}>Close changes</button></header>
      <p id="phase-changes-context">Turn {receipt.turn} · {receipt.boundary === 'COMMIT' ? 'Turn ended' : 'New turn'} · Your patients</p>
      {(gap || (manual && !selected)) && <p role="status">Older updates are no longer available here. Ask Control if you need to see them.</p>}
      {manual && <label>Choose a turn update <select className="input" value={receipt.id} onChange={event => setManual(event.target.value)}>
        {[...v.receipts].reverse().map(update => <option key={update.id} value={update.id}>Turn {update.turn} · {update.boundary === 'COMMIT' ? 'Turn ended' : 'New turn'}</option>)}
      </select></label>}
      <ReceiptContents receipt={receipt} />
      <footer><button className="btn btn-primary" onClick={dismiss}>Continue</button></footer>
    </dialog>}
  </>;
}
